import type { Ring, Vec2 } from '../../../../geo/types';
import { beamSolid, boxSolid, planPoint, subFrame } from '../../solids';
import { openedWall, edgePanel, rectWall } from '../masonry';
import { gableOutline } from '../gothic/roofs';
import { dormerSolid } from '../gothic/dormer';
import { pinnacleSolid } from '../gothic/pinnacle';
import { ridgeTurret } from '../gothic/ridgeTurret';
import { buildHallRoof } from './stadhuisHallRoof';
import {
  crossWindows,
  PINNACLE,
  pointedAt,
  windowedWall,
  type StadhuisBuild,
} from './stadhuisParts';

/** The painting shows two tall upper openings in the pale gabled face beside the tower.
 * Their dimensions, the lower door/window arrangement and moulding sizes are estimates. */
const FRONT = {
  upper: { width: 1.1, sill: 6.2, top: 12.8 },
  lower: { width: 0.65, sill: 1.4, top: 4.2 },
  door: { width: 1.15, top: 3.3 },
  loft: { width: 0.65, sill: 15.8, top: 18.0 },
  mullion: 0.09,
  course: { height: 0.2, outset: 0.12 },
  coping: 0.12,
  pinnacle: { shaft: 0.9, tip: 1.7 },
} as const;

/** The woodcut records rows of windows and dormers on the long hall wall; sizes are inferred. */
const HALL = {
  bay: 4.5,
  rows: [
    { sill: 1.5, top: 4.5, width: 1.35 },
    { sill: 6.4, top: 9.2, width: 1.35 },
  ],
  dormer: { count: 8, width: 1.2, depth: 0.9, setBack: 0.45, eaveRise: 3.0, ridgeRise: 4.0 },
} as const;

function rect(length: number, height: number): Ring {
  return [
    [0, 0],
    [length, 0],
    [length, height],
    [0, height],
  ];
}

function rectangle(center: number, width: number, sill: number, top: number): Ring {
  return [
    [center - width / 2, sill],
    [center + width / 2, sill],
    [center + width / 2, top],
    [center - width / 2, top],
  ];
}

function frontBlock(s: StadhuisBuild, u0: number, u1: number, y0: number, y1: number): void {
  const v = s.plan.hall.v0;
  boxSolid(
    s.builder,
    s.stone.wall,
    s.frame,
    planPoint(u0, y0, v + 0.16),
    planPoint(u1, y1, v - FRONT.course.outset),
    { top: true, bottom: true }
  );
}

/** A whole stone face from the ground to its gable, not a little triangle on a brick hall. */
function buildFront(s: StadhuisBuild): void {
  const h = s.plan.hall;
  const width = h.u1 - h.u0;
  const upper = [width / 3, (2 * width) / 3];
  const lower = [width * 0.18, width * 0.82];
  const holes: Ring[] = [
    ...upper.map((at) => pointedAt(at, FRONT.upper.width, FRONT.upper.sill, FRONT.upper.top)),
    ...lower.map((at) => rectangle(at, FRONT.lower.width, FRONT.lower.sill, FRONT.lower.top)),
    pointedAt(width / 2, FRONT.door.width, 0, FRONT.door.top),
    pointedAt(width / 2, FRONT.loft.width, FRONT.loft.sill, FRONT.loft.top),
  ];
  openedWall(
    s.builder,
    s.stone,
    s.frame,
    edgePanel([h.u0, h.v0], [h.u1, h.v0]),
    gableOutline(width, 0, h.eaveY, s.plan.frontGable.apexY),
    holes
  );
  for (const at of upper) {
    const center = h.u0 + at;
    const m = FRONT.mullion / 2;
    frontBlock(s, center - m, center + m, FRONT.upper.sill, FRONT.upper.top - 1.0);
    frontBlock(
      s,
      center - FRONT.upper.width / 2,
      center + FRONT.upper.width / 2,
      8.2,
      8.2 + FRONT.mullion
    );
  }
  for (const y of [5.1, h.eaveY - FRONT.course.height])
    frontBlock(s, h.u0, h.u1, y, y + FRONT.course.height);
  const center = (h.u0 + h.u1) / 2;
  for (const u of [h.u0, h.u1])
    beamSolid(
      s.builder,
      s.stone.wall,
      s.frame,
      planPoint(u, h.eaveY, h.v0),
      planPoint(center, h.ridgeY, h.v0),
      FRONT.coping
    );
  const p = PINNACLE;
  for (const u of [h.u0 + p.inset, h.u1 - p.inset]) {
    const g = FRONT.pinnacle;
    pinnacleSolid(s.builder, s.stone.wall, s.frame, {
      center: [u, h.v0 + p.inset],
      half: p.half,
      y0: h.eaveY - p.sink,
      shaftTop: h.eaveY + g.shaft,
      tipY: h.eaveY + g.tip,
    });
  }
}

function buildSides(s: StadhuisBuild): void {
  const h = s.plan.hall;
  const depth = h.v1 - h.v0;
  const east: [Vec2, Vec2] = [
    [h.u1, h.v0],
    [h.u1, h.v1],
  ];
  windowedWall(s, s.brick, east, rect(depth, h.eaveY), crossWindows(depth, HALL.bay, HALL.rows));
  const west: [Vec2, Vec2] = [
    [h.u0, h.v1],
    [h.u0, h.v0],
  ];
  const westWindows = crossWindows(depth, HALL.bay, HALL.rows).filter((window) => {
    const v0 = h.v1 - window.x - window.width;
    const v1 = h.v1 - window.x;
    // The tower and hall touch at a masonry joint; that concealed party wall has no windows.
    return v1 <= s.plan.tower.v0 || v0 >= s.plan.tower.v1;
  });
  windowedWall(s, s.brick, west, rect(depth, h.eaveY), westWindows);
  rectWall(
    s.builder,
    s.brick,
    s.frame,
    [
      [h.u1, h.v1],
      [h.u0, h.v1],
    ],
    [0, h.eaveY]
  );
}

/** Dormers face the lane along the long wall, not the narrow Dam-facing gable. */
function buildDormers(s: StadhuisBuild): void {
  const h = s.plan.hall;
  const d = HALL.dormer;
  const frame = subFrame(s.frame, h.u1, h.v0, Math.PI / 2);
  const first = 4;
  const last = h.v1 - h.v0 - 5;
  for (let k = 0; k < d.count; k++) {
    dormerSolid(s.builder, s.brick, frame, {
      u: first + ((last - first) * k) / (d.count - 1),
      v: d.setBack,
      width: d.width,
      depth: d.depth,
      y0: h.eaveY,
      eaveY: h.eaveY + d.eaveRise,
      ridgeY: h.eaveY + d.ridgeRise,
    });
  }
}

export function buildStadhuisHall(s: StadhuisBuild): void {
  buildSides(s);
  buildFront(s);
  buildHallRoof(s);
  buildDormers(s);
  const { turret } = s.plan;
  ridgeTurret(
    s.builder,
    { lantern: s.dark.wall, needle: s.stone.roof, finial: s.stone.wall },
    s.frame,
    { center: [turret.u, turret.v], footY: turret.footY, topY: turret.topY }
  );
}
